import { useUser } from "@saleor/auth";
import Container from "@saleor/components/Container";
import PageHeader from "@saleor/components/PageHeader";
import { defaultListSettings } from "@saleor/config";
import SoldToPage from "@saleor/createOrder/components/SoldToPage";
import {
  contractDetailUrl,
  contractUrl,
  CreateOrderUrlQueryParams
} from "@saleor/createOrder/urls";
import { useCustomerContractsQuery } from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import useListSettings from "@saleor/hooks/useListSettings";
import useLocalPaginator, {
  useLocalPaginationState
} from "@saleor/hooks/useLocalPaginator";
import useNavigator from "@saleor/hooks/useNavigator";
import { ListViews } from "@saleor/types";
import { mapEdgesToItems } from "@saleor/utils/maps";
import { getSortParams, getSortUrlVariables } from "@saleor/utils/sort";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import SoldToList from "../components/SoldToList";
import { getCustomerSortQueryVariables } from "../sort";

interface ContractListProps {
  params: CreateOrderUrlQueryParams;
  id: string;
}

const ContractList: React.FC<ContractListProps> = ({ id, params }) => {
  const [, dispatchAppState] = useAppState();
  const navigate = useNavigator();
  const [contractNo, setContractNo] = useState(id);
  const [filterVariables, setFilterVariables] = useState({
    companyIds: []
  });
  const intl = useIntl();
  const { updateListSettings, settings } = useListSettings(
    ListViews.SOLD_TO_LIST
  );
  useEffect(() => {
    updateListSettings("rowNumber", defaultListSettings.SOLD_TO_LIST.rowNumber);
  }, []);
  const [
    valuesPaginationState,
    setValuesPaginationState
  ] = useLocalPaginationState(settings?.rowNumber);
  const resetPaginationState = () => {
    setValuesPaginationState({
      first: settings?.rowNumber,
      after: undefined,
      before: undefined
    });
  };
  const paginateValues = useLocalPaginator(setValuesPaginationState);
  const { soldToId } = useUser();
  const queryVariables = React.useMemo(
    () => ({
      ...valuesPaginationState,
      contractNo: contractNo || undefined,
      filter: filterVariables,
      soldToId,
      sortBy: getCustomerSortQueryVariables(params)
    }),
    [contractNo, settings.rowNumber, valuesPaginationState, soldToId]
  );
  const { data, loading } = useCustomerContractsQuery({
    displayLoader: true,
    variables: {
      ...queryVariables
    },
    skip: !filterVariables.companyIds.length
  });

  const { loadNextPage, loadPreviousPage, pageInfo } = paginateValues(
    data?.scgpCustomerContracts?.pageInfo,
    valuesPaginationState
  );
  const customerContractNode = useMemo(() => {
    if (data?.scgpCustomerContracts) {
      return mapEdgesToItems(data?.scgpCustomerContracts);
    }
  }, [data?.scgpCustomerContracts]);

  const noFilterInput = useMemo(
    () => filterVariables.companyIds?.[0] === "-1",
    [filterVariables]
  );

  const handleReset = () => {
    setContractNo("");
  };
  const handleChange = value => {
    setContractNo(value);
  };
  const handleViewContract = (id: string) => {
    navigate(contractDetailUrl(id));
  };

  const handleFilterByCompanyIds = (companyIds: string[]) => {
    resetPaginationState();
    setFilterVariables({
      ...filterVariables,
      companyIds: companyIds?.length ? companyIds.map(item => +item) : ["-1"]
    });
  };

  const handleSort = field => {
    resetPaginationState();
    navigate(
      contractUrl(id, {
        ...params,
        ...getSortUrlVariables(field, params)
      }),
      { replace: true }
    );
  };

  useEffect(() => {
    dispatchAppState({
      payload: {
        customerId: contractNo
      },
      type: "setCustomerId"
    });
  }, [contractNo]);

  useEffect(() => {
    resetPaginationState();
  }, [settings?.rowNumber, filterVariables, contractNo]);

  return (
    <>
      <Container>
        <PageHeader
          title={intl.formatMessage({ defaultMessage: "My Contract" })}
        />

        <SoldToPage
          onchange={handleChange}
          onReset={handleReset}
          searchId={contractNo}
          customerContracts={customerContractNode}
          soldToExists={true}
          onFilterByCompanyIds={handleFilterByCompanyIds}
          loading={loading}
        >
          <SoldToList
            settings={settings}
            disabled={loading}
            customerContracts={customerContractNode}
            soldToExists={true}
            noFilterInput={noFilterInput}
            pageInfo={pageInfo}
            onNextPage={loadNextPage}
            onPreviousPage={loadPreviousPage}
            onUpdateListSettings={updateListSettings}
            onRowClick={() => null}
            onViewContract={handleViewContract}
            onSort={handleSort}
            sort={getSortParams(params)}
            totalCount={data?.scgpCustomerContracts?.totalCount}
          />
        </SoldToPage>
      </Container>
    </>
  );
};

export default ContractList;
